import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Plus, Trash2, Copy, Users, ChevronDown, Info, RefreshCw, Monitor } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { API_URL } from "../../config";

const THEMES = [
  { id: "minimalist", name: "Minimalist", badge: "Elegante", preview: "#000" },
  { id: "festive", name: "Festive", badge: "Alegre", preview: "linear-gradient(135deg,#7c2d12,#ea580c,#f59e0b)" },
  { id: "cinematic", name: "Cinematic", badge: "Dramático", preview: "#0a0a0a" },
  { id: "ticker", name: "Ticker", badge: "Dinâmico", preview: "#0b1120" },
  { id: "neon", name: "Neon", badge: "Vibrante", preview: "linear-gradient(135deg,#ec4899,#3b82f6)" },
  { id: "typewriter", name: "Typewriter", badge: "Retro", preview: "#fafaf9" },
  { id: "color", name: "Cor Personalizável", badge: "Cor à Escolha", preview: "linear-gradient(135deg,#1af50a,#0a0a0a)" },
  { id: "custom", name: "Imagem Própria", badge: "Personalizado", preview: "linear-gradient(135deg,#1e293b,#334155)" },
];

export function DisplaySettings({ show, setShow, onSave, saving }) {
  const [publicToken, setPublicToken] = useState("");
  const [displayToken, setDisplayToken] = useState("");
  const publicBackstageUrl = publicToken
    ? `${window.location.origin}/backstage-display?token=${publicToken}`
    : "";
  const displayUrl = displayToken
    ? `${window.location.origin}/display?token=${displayToken}`
    : `${window.location.origin}/display`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bs, disp] = await Promise.all([
          axios.get(`${API_URL}/api/admin/backstage-token`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/display-token`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setPublicToken(bs.data.token || "");
        setDisplayToken(disp.data.token || "");
      } catch (e) { console.error("Failed to load display tokens:", e); }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (field, value) => setShow({ ...show, [field]: value });

  const copyUrl = () => {
    navigator.clipboard.writeText(displayUrl);
    toast.success("URL copiado!");
  };

  const copyPublicBackstage = () => {
    if (!publicBackstageUrl) return;
    navigator.clipboard.writeText(publicBackstageUrl);
    toast.success("URL público copiado!");
  };

  const rotateDisplayToken = async () => {
    if (!window.confirm("Gerar um novo token invalidará o link actual do Ecrã de Check-In. Continuar?")) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/display-token/rotate`, {}, { withCredentials: true });
      setDisplayToken(r.data.token || "");
      toast.success("Novo token gerado para o Ecrã de Check-In!");
    } catch (e) {
      toast.error("Erro ao rotacionar token.");
    }
  };

  const rotatePublicBackstageToken = async () => {
    if (!window.confirm("Gerar um novo token invalidará o link actual. Continuar?")) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/backstage-token/rotate`, {}, { withCredentials: true });
      setPublicToken(r.data.token || "");
      toast.success("Novo token gerado!");
    } catch (e) {
      toast.error("Erro ao rotacionar token.");
    }
  };

  const preview = async (variant) => {
    try {
      await axios.post(`${API_URL}/api/admin/display-preview`, { variant }, { withCredentials: true });
      toast.success("Pré-visualização enviada para o ecrã /display");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const previewTheme = async (themeId) => {
    // Open a dedicated /display window with ?theme=override, then fire a demo checkin event
    const token = displayToken ? `&token=${encodeURIComponent(displayToken)}` : "";
    const url = `${window.location.origin}/display?theme=${encodeURIComponent(themeId)}${token}`;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.error("Permita popups para pré-visualizar o tema.");
      return;
    }
    // Give the new tab ~1.2s to mount + subscribe to SSE before firing the demo event
    setTimeout(async () => {
      try {
        await axios.post(`${API_URL}/api/admin/display-preview`, { variant: "default", first_name: "João", last_name: "Silva" }, { withCredentials: true });
        toast.success(`Tema "${themeId}" em pré-visualização`);
      } catch (e) {
        toast.error("Abri a janela, mas falhou o evento de demo.");
      }
    }, 1200);
  };

  return (
    <div className="space-y-6">
      {/* URL */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10" data-testid="display-url-card">
        <h3 className="text-lg font-semibold text-white mb-1">
          URL do Ecrã de Check-In
        </h3>
        <p className="text-zinc-500 text-sm mb-4">Abra este link no ecrã da entrada do teatro (TV/projector em modo kiosque). Protegido por token — só quem tem o link abre.</p>
        <div className="flex gap-2">
          <Input value={displayUrl} readOnly className="input-dark font-mono text-xs" data-testid="display-url-input" />
          <Button onClick={copyUrl} variant="outline" className="border-white/10 text-zinc-300" data-testid="btn-copy-display-url">
            <Copy className="w-4 h-4 mr-2" /> Copiar
          </Button>
          <a href={displayUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-white/10 text-zinc-300"><ExternalLink className="w-4 h-4 mr-2" /> Abrir</Button>
          </a>
          <Button
            onClick={rotateDisplayToken}
            variant="outline"
            className="border-red-500/30 text-red-300 hover:bg-red-500/10"
            data-testid="btn-rotate-display-token"
            title="Rotacionar token (invalida link actual)"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Novo Token
          </Button>
        </div>
      </div>

      {/* Public Backstage Display URL (tokenized) */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-emerald-500/20" data-testid="backstage-public-url-card">
        <h3 className="text-lg font-semibold text-white mb-1">
          URL do Ecrã de Backstage
        </h3>
        <p className="text-zinc-500 text-sm mb-4">
          Abra este link numa TV/monitor na zona de produção (modo kiosque). Protegido por token único. Mostra ocupação, VIPs, aniversariantes e check-ins recentes (apenas primeiro nome por privacidade). <strong>Não partilhe publicamente</strong> — quem tem o link vê os dados.
        </p>
        <div className="flex gap-2">
          <Input value={publicBackstageUrl || "A gerar…"} readOnly className="input-dark font-mono text-xs" data-testid="backstage-public-url-input" />
          <Button onClick={copyPublicBackstage} variant="outline" className="border-white/10 text-zinc-300" disabled={!publicBackstageUrl} data-testid="btn-copy-public-backstage-url">
            <Copy className="w-4 h-4 mr-2" /> Copiar
          </Button>
          {publicBackstageUrl && (
            <a href={publicBackstageUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white/10 text-zinc-300"><ExternalLink className="w-4 h-4 mr-2" /> Abrir</Button>
            </a>
          )}
          <Button
            onClick={rotatePublicBackstageToken}
            variant="outline"
            className="border-red-500/30 text-red-300 hover:bg-red-500/10"
            data-testid="btn-rotate-backstage-token"
            title="Rotacionar token (invalida link actual)"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Novo Token
          </Button>
        </div>
      </div>

      {/* Themes */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-1">Tema Visual</h3>
        <p className="text-zinc-500 text-sm mb-5">Escolha o estilo principal. As variantes especiais (VIP, aniversário, etc.) sobrepõem-se automaticamente.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {THEMES.map(t => {
            const selected = (show?.display_theme || "minimalist") === t.id;
            return (
              <div
                key={t.id}
                className={`relative rounded-xl overflow-hidden border-2 transition-all text-left group ${selected ? "border-amber-500" : "border-white/10 hover:border-white/30"}`}
              >
                <button
                  onClick={() => update("display_theme", t.id)}
                  className="block w-full text-left"
                  data-testid={`theme-${t.id}`}
                >
                  {t.id === "custom" && show?.display_custom_image_url ? (
                    <div style={{ aspectRatio: "16/9", backgroundImage: `url(${show.display_custom_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  ) : (
                    <div style={{ background: t.preview, aspectRatio: "16/9" }} />
                  )}
                  <div className="px-3 py-2 bg-zinc-950 flex items-center justify-between">
                    <span className="text-white font-medium">{t.name}</span>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{t.badge}</span>
                  </div>
                </button>
                {selected && <span className="absolute top-2 right-2 bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full">ATIVO</span>}
                <button
                  onClick={(e) => { e.stopPropagation(); previewTheme(t.id); }}
                  className="absolute top-2 left-2 bg-black/80 hover:bg-black text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 border border-white/10"
                  data-testid={`preview-theme-${t.id}`}
                  title="Abrir /display numa nova janela com este tema"
                >
                  <ExternalLink className="w-3 h-3" /> Pré-visualizar
                </button>
              </div>
            );
          })}
        </div>

        {/* Custom image URL input (only when theme=custom) */}
        {show?.display_theme === "custom" && (
          <div className="mt-5 p-4 bg-zinc-950 rounded-lg border border-white/10" data-testid="custom-image-panel">
            <Label className="text-zinc-300">URL da Imagem de Fundo <span className="text-amber-400 font-normal">(1920px × 1080px)</span></Label>
            <Input
              value={show?.display_custom_image_url || ""}
              onChange={(e) => update("display_custom_image_url", e.target.value)}
              className="input-dark mt-2"
              placeholder="Cole um link direto, Google Drive ou Box.com"
              data-testid="display-custom-image-url"
            />
            <p className="text-zinc-500 text-xs mt-1">Aceita links diretos, Google Drive e Box.com · proporção widescreen (16:9) em pelo menos 1920×1080.</p>
            <div className="flex items-center gap-3 mt-4">
              <Switch
                checked={show?.display_custom_image_darken !== false}
                onCheckedChange={(v) => update("display_custom_image_darken", v)}
                data-testid="display-custom-darken"
              />
              <span className="text-zinc-300 text-sm">Escurecer a imagem para o nome ficar mais legível</span>
            </div>
          </div>
        )}

        {/* Custom color picker (only when theme=color) */}
        {show?.display_theme === "color" && (
          <div className="mt-5 p-4 bg-zinc-950 rounded-lg border border-white/10" data-testid="custom-color-panel">
            <Label className="text-zinc-300">Cor Principal</Label>
            <div className="flex items-center gap-3 mt-2">
              <input
                type="color"
                value={show?.display_custom_color || "#1af50a"}
                onChange={(e) => update("display_custom_color", e.target.value)}
                className="h-10 w-14 rounded border border-white/10 bg-transparent cursor-pointer"
                data-testid="display-custom-color-picker"
              />
              <Input
                value={show?.display_custom_color || "#1af50a"}
                onChange={(e) => update("display_custom_color", e.target.value)}
                className="input-dark font-mono"
                placeholder="#1af50a"
                data-testid="display-custom-color-hex"
              />
              <div
                className="h-10 w-24 rounded border border-white/10"
                style={{ background: `linear-gradient(135deg, ${show?.display_custom_color || "#1af50a"}, #0a0a0a)` }}
              />
            </div>
            <p className="text-zinc-500 text-xs mt-2">Ex: <code className="text-zinc-300">#1af50a</code>. O fundo do ecrã usa esta cor em gradiente com preto, e o nome aparece em branco.</p>
          </div>
        )}
      </div>

      {/* Message */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-1">Mensagem e Tempo</h3>
        <p className="text-zinc-500 text-sm mb-5">Use placeholders <code className="text-amber-500">{"{primeiro_nome}"}</code>, <code className="text-amber-500">{"{nome}"}</code> ou <code className="text-amber-500">{"{ultimo_nome}"}</code>.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-zinc-300">Template da Mensagem</Label>
            <Input
              value={show?.display_template || ""}
              onChange={(e) => update("display_template", e.target.value)}
              placeholder="Bem-vindo(a) {primeiro_nome}!"
              className="input-dark mt-2"
              data-testid="display-template"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Duração da mensagem (segundos)</Label>
            <Input
              type="number" min={3} max={30}
              value={show?.display_duration || 8}
              onChange={(e) => update("display_duration", parseInt(e.target.value) || 8)}
              className="input-dark mt-2"
              data-testid="display-duration"
            />
          </div>
          <div className="flex items-end gap-3">
            <Switch
              checked={show?.display_sound_enabled !== false}
              onCheckedChange={(v) => update("display_sound_enabled", v)}
              data-testid="display-sound"
            />
            <span className="text-zinc-300 text-sm">Som "ding" ao check-in</span>
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300">Nome do Evento (apenas para este ecrã)</Label>
            <Input
              value={show?.display_show_name_override || ""}
              onChange={(e) => update("display_show_name_override", e.target.value)}
              placeholder={show?.name || "Ex: XTREMO · O MUSICAL"}
              className="input-dark mt-2"
              data-testid="display-show-name-override"
            />
            <p className="text-zinc-500 text-xs mt-1">Aparece em modo repouso, no tema Minimalist (canto) e no tema Ticker (topo). Se vazio, usa o nome do espetáculo.</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300">Activar animações a partir de</Label>
            <Input
              type="datetime-local"
              value={show?.display_active_from || ""}
              onChange={(e) => update("display_active_from", e.target.value)}
              className="input-dark mt-2"
              data-testid="display-active-from"
            />
            <p className="text-zinc-500 text-xs mt-1">
              Se definir uma hora, o ecrã fica em modo repouso até essa hora — mesmo que façam check-ins antes (protege contra animações prematuras durante ensaios/preparação). Deixe vazio para activar sempre.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300">Mensagem quando não há check-ins</Label>
            <Input
              value={show?.display_idle_message || ""}
              onChange={(e) => update("display_idle_message", e.target.value)}
              placeholder="Bem-vindos ao espetáculo"
              className="input-dark mt-2"
              data-testid="display-idle"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300">URL da Imagem em Modo Repouso <span className="text-amber-400 font-normal">(1920px × 1080px)</span></Label>
            <Input
              value={show?.display_idle_image_url || ""}
              onChange={(e) => update("display_idle_image_url", e.target.value)}
              placeholder="Cole um link direto, Google Drive ou Box.com"
              className="input-dark mt-2"
              data-testid="display-idle-image-url"
            />
            <p className="text-zinc-500 text-xs mt-1">Aceita links diretos, Google Drive e Box.com · proporção widescreen (16:9). Se vazio, usa a imagem principal do espetáculo.</p>
          </div>
          <div className="flex items-end gap-3">
            <Switch
              checked={show?.display_idle_show_countdown !== false}
              onCheckedChange={(v) => update("display_idle_show_countdown", v)}
            />
            <span className="text-zinc-300 text-sm">Mostrar próxima sessão em modo repouso</span>
          </div>
        </div>
      </div>

      {/* Variants */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-1">Variantes Especiais</h3>
        <p className="text-zinc-500 text-sm mb-5">O ecrã muda automaticamente quando deteta estas condições.</p>
        <div className="space-y-3">
          {[
            { key: "display_variant_vip_enabled", label: "⭐ VIP / Convidado Especial", hint: "Marcar na edição do bilhete." },
            { key: "display_variant_birthday_enabled", label: "🎂 Aniversariante", hint: "Comparar dia+mês da data de nascimento." },
            { key: "display_variant_first_time_enabled", label: "✨ Primeira Visita", hint: "Primeiro check-in deste email." },
            { key: "display_variant_group_enabled", label: "👨‍👩‍👧 Grupo", hint: "Bilhete associado a um código de grupo." },
          ].map(v => (
            <div key={v.key} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg">
              <div>
                <div className="text-white text-sm">{v.label}</div>
                <div className="text-zinc-500 text-xs">{v.hint}</div>
              </div>
              <Switch
                checked={show?.[v.key] !== false}
                onCheckedChange={(val) => update(v.key, val)}
                data-testid={`toggle-${v.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <h3 className="text-lg font-semibold text-white">Pré-visualização no Ecrã</h3>
          <ScreensCountBadge />
        </div>
        <p className="text-zinc-500 text-sm mb-4">Abra o ecrã <code className="text-amber-500">/display</code> noutra janela e clique para disparar uma simulação. Botões "Testar" funcionam mesmo com a hora de activação definida.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => preview("default")} variant="outline" className="border-white/10 text-zinc-300" data-testid="preview-default">Normal</Button>
          <Button onClick={() => preview("vip")} variant="outline" className="border-amber-500/40 text-amber-400" data-testid="preview-vip">⭐ VIP</Button>
          <Button onClick={() => preview("birthday")} variant="outline" className="border-pink-500/40 text-pink-400" data-testid="preview-birthday">🎂 Aniversário</Button>
          <Button onClick={() => preview("first_time")} variant="outline" className="border-emerald-500/40 text-emerald-400" data-testid="preview-first">✨ 1ª Vez</Button>
          <Button onClick={() => preview("group")} variant="outline" className="border-indigo-500/40 text-indigo-400" data-testid="preview-group">👨‍👩‍👧 Grupo</Button>
        </div>
      </div>

      {/* Groups */}
      <GroupManagement />

      {/* Setup Guide (last position) */}
      <InstallGuide displayUrl={displayUrl} />

      {/* Save button */}
      {onSave && (
        <Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="btn-save-display-config">
          {saving ? "A guardar..." : "Guardar Configurações do Ecrã"}
        </Button>
      )}
    </div>
  );
}

function InstallGuide({ displayUrl }) {
  const [open, setOpen] = useState(false);
  const steps = [
    {
      t: "1. Escolha o hardware",
      d: "Qualquer dispositivo com um browser moderno funciona. Os mais práticos e económicos:",
      items: [
        "📺 TV Smart com browser (Samsung/LG)",
        "🔴 Amazon Fire TV Stick (~30€) — plug into any HDMI TV",
        "🟡 Chromecast com Google TV (~40€) — apresentar a partir do telemóvel",
        "💻 Qualquer PC ou portátil velho ligado à TV via HDMI",
        "🍓 Raspberry Pi (para instalação fixa profissional)",
      ],
    },
    {
      t: "2. Modo kiosque (fullscreen)",
      d: "Para que o ecrã fique limpo sem barras de endereço:",
      items: [
        "Chrome no PC: abrir o URL → pressionar F11 (fullscreen)",
        "Chrome no Android: ▸ menu → \"Adicionar à página inicial\" → abre como app sem barras",
        "Fire TV Stick: instalar a app \"Silk Browser\" da Amazon e abrir o URL",
        "Raspberry Pi: instalar Chromium e iniciar com `chromium --kiosk <URL>`",
      ],
    },
    {
      t: "3. Internet estável",
      d: "O ecrã usa Server-Sent Events (SSE) — precisa de conexão contínua. Se a rede falhar, o ecrã volta ao modo repouso automaticamente e reconecta.",
      items: [],
    },
    {
      t: "4. Posicionamento",
      d: "Sugestões de onde colocar o ecrã:",
      items: [
        "Por cima da porta principal (grande TV horizontal)",
        "Ao lado do segurança do check-in (para efeito imediato)",
        "Projector na parede da entrada (para efeito máximo)",
      ],
    },
    {
      t: "5. Fazer testes antes do evento",
      d: "Na mesma aba, use os botões de \"Pré-visualizar\" para ver cada variante (VIP, Aniversário, 1ª Vez, Grupo) a funcionar no ecrã grande.",
      items: [],
    },
  ];

  return (
    <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden" data-testid="install-guide">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
        data-testid="install-guide-toggle"
      >
        <div className="flex items-center gap-3">
          <Info className="w-5 h-5 text-amber-500" />
          <div>
            <h3 className="text-lg font-semibold text-white">Como instalar Ecrans</h3>
            <p className="text-zinc-500 text-sm">Guia passo-a-passo para colocar a funcionar</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-white/10 pt-4 space-y-5">
          {steps.map((s, i) => (
            <div key={i}>
              <div className="text-white font-medium mb-1">{s.t}</div>
              <div className="text-zinc-400 text-sm mb-2">{s.d}</div>
              {s.items.length > 0 && (
                <ul className="space-y-1 pl-4">
                  {s.items.map((it, j) => (
                    <li key={j} className="text-zinc-400 text-sm">{it}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="text-amber-300 text-sm font-medium mb-1">💡 Dica pro</div>
            <div className="text-zinc-300 text-sm">
              Para teatros que fazem vários eventos por mês, deixe um Raspberry Pi fixo atrás da TV da entrada, sempre ligado e com este URL em loop no Chromium kiosque. Custo único de ~50€ e pronto para qualquer evento futuro sem configuração.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupManagement() {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", description: "" });

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/groups`, { withCredentials: true });
      setGroups(r.data);
    } catch (e) { console.error("Failed to load groups:", e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/admin/groups/${editing}`, form, { withCredentials: true });
        toast.success("Grupo atualizado");
      } else {
        await axios.post(`${API_URL}/api/admin/groups`, form, { withCredentials: true });
        toast.success("Grupo criado");
      }
      setForm({ code: "", name: "", description: "" }); setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar este grupo?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/groups/${id}`, { withCredentials: true });
      toast.success("Grupo eliminado");
      load();
    } catch (e) { toast.error("Erro"); }
  };

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-white/10" data-testid="groups-card">
      <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2"><Users className="w-5 h-5" /> Grupos</h3>
      <p className="text-zinc-500 text-sm mb-5">Crie códigos de grupo (ex: <code className="text-amber-500">FAMILIA-SILVA</code>). Quando um bilhete tem um código de grupo, o ecrã mostra o nome do grupo em vez do nome individual.</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <Input placeholder="Código (ex: FAMILIA)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input-dark" data-testid="group-code" />
        <Input placeholder="Nome para mostrar" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark md:col-span-2" data-testid="group-name" />
        <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="btn-save-group">
          <Plus className="w-4 h-4 mr-2" /> {editing ? "Atualizar" : "Criar"}
        </Button>
      </div>

      <div className="space-y-2">
        {groups.length === 0 && <div className="text-zinc-500 text-sm text-center py-6">Ainda não tem grupos criados.</div>}
        {groups.map(g => (
          <div key={g.id} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg" data-testid={`group-${g.code}`}>
            <div>
              <div className="text-white font-medium">{g.name} <span className="text-zinc-500 text-xs ml-2 font-mono">{g.code}</span></div>
              <div className="text-zinc-500 text-xs">{g.members_count || 0} inscritos</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-white/10 text-zinc-300" onClick={() => { setEditing(g.id); setForm({ code: g.code, name: g.name, description: g.description || "" }); }}>Editar</Button>
              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => del(g.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScreensCountBadge() {
  const [count, setCount] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/display/screens-count`, { withCredentials: true });
      setCount(r.data.count);
    } catch (_) { setCount(null); }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, []);

  if (count === null) return null;

  const color =
    count > 0
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      : "border-zinc-700 bg-zinc-800 text-zinc-400";

  return (
    <button
      type="button"
      onClick={refresh}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${color}`}
      data-testid="display-screens-count"
      title="Número de ecrãs ligados em tempo real (actualiza a cada 5s)"
    >
      <Monitor className="w-3.5 h-3.5" />
      <span className="font-medium">
        {count === 0 ? "Nenhum ecrã ligado" : `${count} ${count === 1 ? "ecrã" : "ecrãs"} ligado${count === 1 ? "" : "s"}`}
      </span>
    </button>
  );
}

